import { setTraits, ChildOf, Pair, Transform, Mesh, MeshShape, defineRelation, type Plugin, type State, type System } from '@multiplekex/shallot';
import { TARGET_MODEL, DRAFT_MODEL, VALID, INVALID } from './colors';

export const ProbabilityDistribution = {
  barCount: [] as number[],
  transition: [] as number[],
  blend: [] as number[],
  smoothness: [] as number[],
  comparisonTransition: [] as number[],
  comparisonZ: [] as number[],
  acceptanceTransition: [] as number[],
  acceptanceYOffset: [] as number[],
  acceptanceSize: [] as number[],
  residualTransition: [] as number[],
  minTransition: [] as number[],
  excessDraftTransition: [] as number[],
  excessTargetTransition: [] as number[],
  transferProgress: [] as number[],
};

setTraits(ProbabilityDistribution, {
  defaults: () => ({
    barCount: 5,
    transition: 1,
    blend: 0,
    smoothness: 0.15,
    comparisonTransition: 0,
    comparisonZ: -0.6,
    acceptanceTransition: 0,
    acceptanceYOffset: 0.5,
    acceptanceSize: 0.35,
    residualTransition: 0,
    minTransition: 0,
    excessDraftTransition: 0,
    excessTargetTransition: 0,
    transferProgress: 0,
  }),
});

export const DistributionBar = {
  index: [] as number[],
  probability: [] as number[],
  goalProbability: [] as number[],
};

setTraits(DistributionBar, {
  defaults: () => ({
    index: 0,
    probability: 0.2,
    goalProbability: 0.2,
  }),
});

const DistributionBarOwner = defineRelation('distribution-bar-owner', { exclusive: true });
const ComparisonBarOwner = defineRelation('comparison-bar-owner', { exclusive: true });
const AcceptanceMarkerOwner = defineRelation('acceptance-marker-owner', { exclusive: true });
const ResidualBarOwner = defineRelation('residual-bar-owner', { exclusive: true });
const MinBarOwner = defineRelation('min-bar-owner', { exclusive: true });
const ExcessDraftBarOwner = defineRelation('excess-draft-bar-owner', { exclusive: true });
const ExcessTargetBarOwner = defineRelation('excess-target-bar-owner', { exclusive: true });

export const AcceptanceMarker = {
  index: [] as number[],
};

setTraits(AcceptanceMarker, {
  defaults: () => ({
    index: 0,
  }),
});

export const BarHighlight = {
  distribution: [] as number[],
  barIndex: [] as number[],
  yOffset: [] as number[],
};

setTraits(BarHighlight, {
  defaults: () => ({
    distribution: 0,
    barIndex: 0,
    yOffset: 0.7,
  }),
});

const targetDistributions = new Map<number, number[]>();
const draftDistributions = new Map<number, number[]>();

export function setDistributionProbabilities(distEid: number, targetProbs: number[], draftProbs?: number[]): void {
  targetDistributions.set(distEid, targetProbs);
  if (draftProbs) {
    draftDistributions.set(distEid, draftProbs);
  }
}

function lerpColor(c1: number, c2: number, t: number): number {
  const r1 = (c1 >> 16) & 0xff;
  const g1 = (c1 >> 8) & 0xff;
  const b1 = c1 & 0xff;
  const r2 = (c2 >> 16) & 0xff;
  const g2 = (c2 >> 8) & 0xff;
  const b2 = c2 & 0xff;
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return (r << 16) | (g << 8) | b;
}

const RED = 0xdc5858;
const YELLOW = 0xe5a040;
const GREEN = 0x3fb87a;

function acceptanceColor(acceptance: number): number {
  // Nonlinear: yellow at 90%, making lower acceptance more obvious
  if (acceptance < 0.9) {
    return lerpColor(RED, YELLOW, acceptance / 0.9);
  } else {
    return lerpColor(YELLOW, GREEN, (acceptance - 0.9) / 0.1);
  }
}

const CHART_WIDTH = 8;
const MAX_HEIGHT = 5.5;

function smoothLerp(smoothness: number, dt: number): number {
  const s = Math.max(0, Math.min(1, smoothness));
  return 1 - Math.pow(1 - s, dt * 60);
}

const ProbabilityDistributionSystem: System = {
  group: 'simulation',
  update(state: State) {
    const dt = state.time.deltaTime;

    for (const distEid of state.query([ProbabilityDistribution, Transform])) {
      const barCount = ProbabilityDistribution.barCount[distEid];
      const transition = ProbabilityDistribution.transition[distEid];
      const blend = ProbabilityDistribution.blend[distEid];
      const smoothness = ProbabilityDistribution.smoothness[distEid];
      const comparisonTransition = ProbabilityDistribution.comparisonTransition[distEid];
      const comparisonZ = ProbabilityDistribution.comparisonZ[distEid];

      const existingBars = [...state.query([Pair(DistributionBarOwner.relation, distEid), DistributionBar, Mesh, Transform])];

      while (existingBars.length < barCount) {
        const barEid = state.addEntity();
        state.addComponent(barEid, Transform);
        state.addComponent(barEid, Mesh);
        state.addComponent(barEid, DistributionBar);
        state.addComponent(barEid, Pair(ChildOf.relation, distEid));
        state.addComponent(barEid, Pair(DistributionBarOwner.relation, distEid));

        Mesh.shape[barEid] = MeshShape.Box;
        DistributionBar.index[barEid] = existingBars.length;
        existingBars.push(barEid);
      }

      const barSpacing = CHART_WIDTH / barCount;
      const barWidth = barSpacing * 0.7;
      const startX = -CHART_WIDTH / 2 + barSpacing / 2;

      const targetProbs = targetDistributions.get(distEid);
      const draftProbs = draftDistributions.get(distEid);
      const t = smoothLerp(smoothness, dt);
      const color = lerpColor(TARGET_MODEL, DRAFT_MODEL, blend);

      for (let i = 0; i < barCount; i++) {
        const barEid = existingBars[i];

        const targetP = targetProbs && targetProbs.length > i ? targetProbs[i] : 0.2;
        const draftP = draftProbs && draftProbs.length > i ? draftProbs[i] : targetP;
        const goalP = targetP + (draftP - targetP) * blend;

        DistributionBar.goalProbability[barEid] = goalP;
        DistributionBar.probability[barEid] += (goalP - DistributionBar.probability[barEid]) * t;

        const probability = DistributionBar.probability[barEid];
        const height = Math.max(0.05, probability * MAX_HEIGHT);

        Transform.posX[barEid] = startX + i * barSpacing;
        Transform.posY[barEid] = (height / 2) * transition;
        Transform.posZ[barEid] = 0;

        Mesh.sizeX[barEid] = barWidth * transition;
        Mesh.sizeY[barEid] = height * transition;
        Mesh.sizeZ[barEid] = barWidth * transition;

        Mesh.color[barEid] = color;
      }

      const existingComparisonBars = [...state.query([Pair(ComparisonBarOwner.relation, distEid), Mesh, Transform])];

      if (comparisonTransition > 0) {
        while (existingComparisonBars.length < barCount) {
          const barEid = state.addEntity();
          state.addComponent(barEid, Transform);
          state.addComponent(barEid, Mesh);
          state.addComponent(barEid, Pair(ChildOf.relation, distEid));
          state.addComponent(barEid, Pair(ComparisonBarOwner.relation, distEid));
          Mesh.shape[barEid] = MeshShape.Box;
          existingComparisonBars.push(barEid);
        }

        for (let i = 0; i < barCount; i++) {
          const compBarEid = existingComparisonBars[i];
          const targetP = targetProbs && targetProbs.length > i ? targetProbs[i] : 0.2;
          const height = Math.max(0.05, targetP * MAX_HEIGHT);

          Transform.posX[compBarEid] = startX + i * barSpacing;
          Transform.posY[compBarEid] = (height / 2) * comparisonTransition;
          Transform.posZ[compBarEid] = comparisonZ;

          Mesh.sizeX[compBarEid] = barWidth * comparisonTransition;
          Mesh.sizeY[compBarEid] = height * comparisonTransition;
          Mesh.sizeZ[compBarEid] = barWidth * comparisonTransition;

          Mesh.color[compBarEid] = TARGET_MODEL;
        }
      } else {
        for (const compBarEid of existingComparisonBars) {
          Mesh.sizeX[compBarEid] = 0;
          Mesh.sizeY[compBarEid] = 0;
          Mesh.sizeZ[compBarEid] = 0;
        }
      }

      const acceptanceTransition = ProbabilityDistribution.acceptanceTransition[distEid];
      const acceptanceYOffset = ProbabilityDistribution.acceptanceYOffset[distEid];
      const acceptanceSize = ProbabilityDistribution.acceptanceSize[distEid];

      const existingMarkers = [...state.query([Pair(AcceptanceMarkerOwner.relation, distEid), AcceptanceMarker, Mesh, Transform])];

      if (acceptanceTransition > 0) {
        while (existingMarkers.length < barCount) {
          const markerEid = state.addEntity();
          state.addComponent(markerEid, Transform);
          state.addComponent(markerEid, Mesh);
          state.addComponent(markerEid, AcceptanceMarker);
          state.addComponent(markerEid, Pair(ChildOf.relation, distEid));
          state.addComponent(markerEid, Pair(AcceptanceMarkerOwner.relation, distEid));
          Mesh.shape[markerEid] = MeshShape.Sphere;
          AcceptanceMarker.index[markerEid] = existingMarkers.length;
          existingMarkers.push(markerEid);
        }

        for (let i = 0; i < barCount; i++) {
          const markerEid = existingMarkers[i];
          const targetP = targetProbs && targetProbs.length > i ? targetProbs[i] : 0.2;
          const draftP = draftProbs && draftProbs.length > i ? draftProbs[i] : targetP;
          const maxHeight = Math.max(targetP, draftP) * MAX_HEIGHT;
          const acceptance = draftP > 0 ? Math.min(1, targetP / draftP) : 1;

          Transform.posX[markerEid] = startX + i * barSpacing;
          Transform.posY[markerEid] = maxHeight + acceptanceYOffset;
          Transform.posZ[markerEid] = comparisonZ / 2;

          const scaledSize = acceptanceSize * acceptanceTransition;
          Mesh.sizeX[markerEid] = scaledSize;
          Mesh.sizeY[markerEid] = scaledSize;
          Mesh.sizeZ[markerEid] = scaledSize;

          Mesh.color[markerEid] = acceptanceColor(acceptance);
        }
      } else {
        for (const markerEid of existingMarkers) {
          Mesh.sizeX[markerEid] = 0;
          Mesh.sizeY[markerEid] = 0;
          Mesh.sizeZ[markerEid] = 0;
        }
      }

      const residualTransition = ProbabilityDistribution.residualTransition[distEid];
      const existingResidualBars = [...state.query([Pair(ResidualBarOwner.relation, distEid), Mesh, Transform])];

      if (residualTransition > 0) {
        while (existingResidualBars.length < barCount) {
          const barEid = state.addEntity();
          state.addComponent(barEid, Transform);
          state.addComponent(barEid, Mesh);
          state.addComponent(barEid, Pair(ChildOf.relation, distEid));
          state.addComponent(barEid, Pair(ResidualBarOwner.relation, distEid));
          Mesh.shape[barEid] = MeshShape.Box;
          existingResidualBars.push(barEid);
        }

        for (let i = 0; i < barCount; i++) {
          const barEid = existingResidualBars[i];
          const targetP = targetProbs && targetProbs.length > i ? targetProbs[i] : 0.2;
          const draftP = draftProbs && draftProbs.length > i ? draftProbs[i] : targetP;
          const residualP = Math.max(0, targetP - draftP);

          if (residualP <= 0.001) {
            Mesh.sizeX[barEid] = 0;
            Mesh.sizeY[barEid] = 0;
            Mesh.sizeZ[barEid] = 0;
            continue;
          }

          const height = residualP * MAX_HEIGHT * residualTransition;

          Transform.posX[barEid] = startX + i * barSpacing;
          Transform.posY[barEid] = height / 2;
          Transform.posZ[barEid] = 0;

          Mesh.sizeX[barEid] = barWidth * residualTransition;
          Mesh.sizeY[barEid] = height;
          Mesh.sizeZ[barEid] = barWidth * residualTransition;

          Mesh.color[barEid] = 0x9333ea;
        }
      } else {
        for (const barEid of existingResidualBars) {
          Mesh.sizeX[barEid] = 0;
          Mesh.sizeY[barEid] = 0;
          Mesh.sizeZ[barEid] = 0;
        }
      }

      // Min bars (green/accepted) - from y=0 to min(draft, target)
      const minTransition = ProbabilityDistribution.minTransition[distEid];
      const existingMinBars = [...state.query([Pair(MinBarOwner.relation, distEid), Mesh, Transform])];

      if (minTransition > 0) {
        while (existingMinBars.length < barCount) {
          const barEid = state.addEntity();
          state.addComponent(barEid, Transform);
          state.addComponent(barEid, Mesh);
          state.addComponent(barEid, Pair(ChildOf.relation, distEid));
          state.addComponent(barEid, Pair(MinBarOwner.relation, distEid));
          Mesh.shape[barEid] = MeshShape.Box;
          existingMinBars.push(barEid);
        }

        for (let i = 0; i < barCount; i++) {
          const barEid = existingMinBars[i];
          const targetP = targetProbs && targetProbs.length > i ? targetProbs[i] : 0.2;
          const draftP = draftProbs && draftProbs.length > i ? draftProbs[i] : targetP;
          const minP = Math.min(targetP, draftP);

          const height = Math.max(0.05, minP * MAX_HEIGHT * minTransition);

          Transform.posX[barEid] = startX + i * barSpacing;
          Transform.posY[barEid] = height / 2;
          Transform.posZ[barEid] = 0;

          Mesh.sizeX[barEid] = barWidth * minTransition;
          Mesh.sizeY[barEid] = height;
          Mesh.sizeZ[barEid] = barWidth * minTransition;

          Mesh.color[barEid] = VALID;
        }
      } else {
        for (const barEid of existingMinBars) {
          Mesh.sizeX[barEid] = 0;
          Mesh.sizeY[barEid] = 0;
          Mesh.sizeZ[barEid] = 0;
        }
      }

      // Excess draft bars (red/rejected) - from y=min to y=draft when draft > target
      const excessDraftTransition = ProbabilityDistribution.excessDraftTransition[distEid];
      const transferProgress = ProbabilityDistribution.transferProgress[distEid];
      const existingExcessDraftBars = [...state.query([Pair(ExcessDraftBarOwner.relation, distEid), Mesh, Transform])];

      if (excessDraftTransition > 0) {
        while (existingExcessDraftBars.length < barCount) {
          const barEid = state.addEntity();
          state.addComponent(barEid, Transform);
          state.addComponent(barEid, Mesh);
          state.addComponent(barEid, Pair(ChildOf.relation, distEid));
          state.addComponent(barEid, Pair(ExcessDraftBarOwner.relation, distEid));
          Mesh.shape[barEid] = MeshShape.Box;
          existingExcessDraftBars.push(barEid);
        }

        for (let i = 0; i < barCount; i++) {
          const barEid = existingExcessDraftBars[i];
          const targetP = targetProbs && targetProbs.length > i ? targetProbs[i] : 0.2;
          const draftP = draftProbs && draftProbs.length > i ? draftProbs[i] : targetP;
          const minP = Math.min(targetP, draftP);
          const excessP = Math.max(0, draftP - targetP);

          if (excessP <= 0.001) {
            Mesh.sizeX[barEid] = 0;
            Mesh.sizeY[barEid] = 0;
            Mesh.sizeZ[barEid] = 0;
            continue;
          }

          // Smooth fade out as transfer progresses - scale all dimensions
          const fadeScale = 1 - transferProgress;
          const height = excessP * MAX_HEIGHT * excessDraftTransition * fadeScale;
          const baseHeight = minP * MAX_HEIGHT;
          const widthScale = barWidth * excessDraftTransition * fadeScale;

          Transform.posX[barEid] = startX + i * barSpacing;
          Transform.posY[barEid] = baseHeight + height / 2;
          Transform.posZ[barEid] = 0;

          Mesh.sizeX[barEid] = widthScale;
          Mesh.sizeY[barEid] = height;
          Mesh.sizeZ[barEid] = widthScale;

          Mesh.color[barEid] = INVALID;
        }
      } else {
        for (const barEid of existingExcessDraftBars) {
          Mesh.sizeX[barEid] = 0;
          Mesh.sizeY[barEid] = 0;
          Mesh.sizeZ[barEid] = 0;
        }
      }

      // Excess target bars (purple) - from y=min to y=target when target > draft
      const excessTargetTransition = ProbabilityDistribution.excessTargetTransition[distEid];
      const existingExcessTargetBars = [...state.query([Pair(ExcessTargetBarOwner.relation, distEid), Mesh, Transform])];

      if (excessTargetTransition > 0) {
        while (existingExcessTargetBars.length < barCount) {
          const barEid = state.addEntity();
          state.addComponent(barEid, Transform);
          state.addComponent(barEid, Mesh);
          state.addComponent(barEid, Pair(ChildOf.relation, distEid));
          state.addComponent(barEid, Pair(ExcessTargetBarOwner.relation, distEid));
          Mesh.shape[barEid] = MeshShape.Box;
          existingExcessTargetBars.push(barEid);
        }

        for (let i = 0; i < barCount; i++) {
          const barEid = existingExcessTargetBars[i];
          const targetP = targetProbs && targetProbs.length > i ? targetProbs[i] : 0.2;
          const draftP = draftProbs && draftProbs.length > i ? draftProbs[i] : targetP;
          const minP = Math.min(targetP, draftP);
          const excessP = Math.max(0, targetP - draftP);

          if (excessP <= 0.001) {
            Mesh.sizeX[barEid] = 0;
            Mesh.sizeY[barEid] = 0;
            Mesh.sizeZ[barEid] = 0;
            continue;
          }

          // Fade in as transfer progresses
          const fadeScale = transferProgress;
          const height = excessP * MAX_HEIGHT * excessTargetTransition * fadeScale;
          const baseHeight = minP * MAX_HEIGHT;

          Transform.posX[barEid] = startX + i * barSpacing;
          Transform.posY[barEid] = baseHeight + height / 2;
          Transform.posZ[barEid] = 0;

          Mesh.sizeX[barEid] = barWidth * excessTargetTransition;
          Mesh.sizeY[barEid] = height;
          Mesh.sizeZ[barEid] = barWidth * excessTargetTransition;

          Mesh.color[barEid] = 0x9333ea; // Purple
        }
      } else {
        for (const barEid of existingExcessTargetBars) {
          Mesh.sizeX[barEid] = 0;
          Mesh.sizeY[barEid] = 0;
          Mesh.sizeZ[barEid] = 0;
        }
      }
    }
  },
};

const BarHighlightSystem: System = {
  group: 'simulation',
  update(state: State) {
    for (const highlightEid of state.query([BarHighlight, Transform])) {
      const distEid = BarHighlight.distribution[highlightEid];
      const barIndex = BarHighlight.barIndex[highlightEid];
      const yOffset = BarHighlight.yOffset[highlightEid];

      if (!state.hasComponent(distEid, ProbabilityDistribution)) continue;

      const barCount = ProbabilityDistribution.barCount[distEid];

      const bars = [...state.query([Pair(DistributionBarOwner.relation, distEid), DistributionBar])];
      const barEid = bars.find(eid => DistributionBar.index[eid] === barIndex);
      if (barEid === undefined) continue;

      const probability = DistributionBar.probability[barEid];

      const barSpacing = CHART_WIDTH / barCount;
      const startX = -CHART_WIDTH / 2 + barSpacing / 2;
      const barX = startX + barIndex * barSpacing;
      const barHeight = probability * MAX_HEIGHT;

      Transform.posX[highlightEid] = barX;
      Transform.posY[highlightEid] = 0.5 + barHeight + yOffset;
    }
  },
};

export const ProbabilityDistributionPlugin: Plugin = {
  components: { ProbabilityDistribution, DistributionBar, BarHighlight, AcceptanceMarker },
  systems: [ProbabilityDistributionSystem, BarHighlightSystem],
};
